/**
 * LeadDetailHeader — sticky header with prospect info, stage stepper, advance buttons.
 * Extracted from LeadDetailPage.tsx (iteration 4 refactor-split).
 * New: sticky positioning, stage stepper (display-only, US-71).
 */

import { useEffect, useRef, useState } from 'react';
import { Copy, Check } from 'lucide-react';
import { Button } from '../../../components/ui/button';
import { badgeVariants } from '../../../components/ui/badge-variants';
import { ImproveAgentButton } from '../../../components/agent/improvement/ImprovementEntryButton';
import { ScrollFadeTabs } from '../../../components/ui/ScrollFadeTabs';
import { mintScheduleToken, sendApplicationLink, sendInitialApproval } from '../../../lib/leasing.api';
import { useCopyToClipboard } from '../../../hooks/useCopyToClipboard';
import { usePropertyTimezone } from '../../../hooks/usePropertyTimezone';
import { formatTimestamp } from '../../../utils/date';
import { asIsoDateTimeString } from '@resida/shared';
import type { LeadDetail, LeadStage, Applicant } from './shared';
import {
  STAGE_LABELS,
  STAGE_COLORS,
  VALID_TRANSITIONS,
  SCHEDULING_LINK_STAGES,
  canAdvanceToVerifying,
  canAdvanceToDeciding,
} from './shared-constants';
import { DecisionModal } from './modals/DecisionModal';
import { SiblingCloseModal } from './modals/SiblingCloseModal';
import { SchedulingLinkModal } from './modals/SchedulingLinkModal';
import { ClosedLeadControls } from './ClosedLeadControls';

// Stage stepper order
const STAGE_ORDER_LIST: LeadStage[] = ['inquiring', 'showing', 'applying', 'verifying', 'deciding', 'closed'];

const STAGE_ORDER: Record<LeadStage, number> = {
  inquiring: 0,
  showing: 1,
  applying: 2,
  verifying: 3,
  deciding: 4,
  closed: 5,
};

export interface LeadDetailHeaderProps {
  lead: LeadDetail;
  applicants: Applicant[];
  onAdvance: (to: LeadStage) => void;
  isAdvancing: boolean;
  isPlatformAdmin: boolean;
  onLeadUpdated: () => void;
}

export function LeadDetailHeader({ lead, applicants, onAdvance, isAdvancing, isPlatformAdmin, onLeadUpdated }: LeadDetailHeaderProps) {
  const transitions = VALID_TRANSITIONS[lead.stage] ?? [];
  const isDeciding = lead.stage === 'deciding';
  const isClosed = lead.stage === 'closed';

  // Compute gating conditions once for use in render
  const verifyingEnabled = canAdvanceToVerifying(applicants);
  const decidingEnabled = canAdvanceToDeciding(applicants);
  const [showDecisionModal, setShowDecisionModal] = useState(false);
  const [siblingPrompt, setSiblingPrompt] = useState<{ count: number; ids: string[] } | null>(null);
  // AC-L14: `position: sticky` cannot work here. AppShell's `<main>` (out of this
  // component's scope) sets `overflow-x-hidden` with no explicit `overflow-y` — per the
  // CSS Overflow spec, when one axis is non-visible the other is forced to `auto`, which
  // makes `<main>` (not the actual scrolling element, `document.scrollingElement`/`<html>`)
  // the sticky positioning containing block. `<main>` itself never scrolls (its own box
  // just grows with content; the document scrolls past it), so native sticky never
  // engages — confirmed live at every breakpoint. This replicates sticky behavior
  // manually: a zero-height sentinel marks where the header sits in normal flow, an
  // IntersectionObserver watches it against a root shrunk by the same top offset the old
  // (non-functional) `sticky` class used, and once the sentinel scrolls past that line the
  // header switches to `fixed`, matching `<main>`'s own sidebar/workflow-rail insets so it
  // lines up identically to before. A placeholder holds the header's flow height while
  // pinned so surrounding content doesn't jump.
  const headerRef = useRef<HTMLElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [isPinned, setIsPinned] = useState(false);
  const [pinnedHeight, setPinnedHeight] = useState(0);
  // AC-L5: the 3 send-CTA buttons (+ their status/hint paragraphs) below add real height
  // to this sticky mobile header, pushing the active tab's own content below the initial
  // viewport. Collapsed by default at <768px (md+ always shows them — there's room).
  const [actionsExpanded, setActionsExpanded] = useState(false);
  const [isMintingScheduleLink, setIsMintingScheduleLink] = useState(false);
  const [schedulingLinkModal, setSchedulingLinkModal] = useState<{ tokenUrl: string; expiresAt: string } | null>(null);
  const [schedulingLinkError, setSchedulingLinkError] = useState<string | null>(null);

  // Send application link (Change 16 / AC-4)
  const [isSendingApplicationLink, setIsSendingApplicationLink] = useState(false);
  const [applicationLinkSentTo, setApplicationLinkSentTo] = useState<string | null>(null);
  const [applicationLinkError, setApplicationLinkError] = useState<string | null>(null);

  // Send initial approval (US-IA-1)
  const [isSendingInitialApproval, setIsSendingInitialApproval] = useState(false);
  const [initialApprovalError, setInitialApprovalError] = useState<string | null>(null);
  const [initialApprovalNotifiedCount, setInitialApprovalNotifiedCount] = useState<number | null>(null);

  const emailCopy = useCopyToClipboard();
  const phoneCopy = useCopyToClipboard();
  const propertyTimezone = usePropertyTimezone();

  // AC-L14: keep the placeholder's reserved height in sync with the header's actual
  // rendered height (it changes as the mobile actions toggle expands/collapses, errors
  // appear, etc.) whether or not the header is currently pinned.
  useEffect(() => {
    const header = headerRef.current;
    if (!header || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver((entries) => {
      const height = entries[0]?.contentRect.height;
      if (height) setPinnedHeight(height);
    });
    observer.observe(header);
    return () => observer.disconnect();
  }, []);

  // AC-L14: manual sticky — see comment above `isPinned`'s declaration for why native
  // `position: sticky` doesn't work inside AppShell's `<main>`.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    if (!sentinel || typeof IntersectionObserver === 'undefined') return;

    // Mirrors the (non-functional) `top-[var(--topbar-height)] md:top-0` sticky offset:
    // below the `md` breakpoint the header should pin beneath the mobile TopBar; at md+
    // there is no mobile TopBar, so it pins flush to the viewport top.
    const getTopOffsetPx = () => {
      if (window.matchMedia('(min-width: 768px)').matches) return 0;
      const rootFontSizePx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const raw = getComputedStyle(document.documentElement).getPropertyValue('--topbar-height').trim();
      return raw.endsWith('rem') ? parseFloat(raw) * rootFontSizePx : parseFloat(raw) || 0;
    };

    let observer: IntersectionObserver;
    const connect = () => {
      observer = new IntersectionObserver(
        ([entry]) => {
          if (entry) setIsPinned(!entry.isIntersecting);
        },
        { rootMargin: `-${getTopOffsetPx()}px 0px 0px 0px`, threshold: 0 }
      );
      observer.observe(sentinel);
    };
    connect();

    // Reconnect with a recomputed offset when crossing the `md` breakpoint.
    const mql = window.matchMedia('(min-width: 768px)');
    const onBreakpointChange = () => {
      observer.disconnect();
      connect();
    };
    mql.addEventListener('change', onBreakpointChange);

    return () => {
      observer.disconnect();
      mql.removeEventListener('change', onBreakpointChange);
    };
  }, []);

  const canSendSchedulingLink = SCHEDULING_LINK_STAGES.includes(lead.stage);
  const isLeadClosed = lead.stage === 'closed';
  const missingProspectName = !lead.prospectName;
  const sendApplicationLinkDisabled = isLeadClosed || missingProspectName || isSendingApplicationLink;
  const sendApplicationLinkDisabledReason = isLeadClosed
    ? 'Lead is closed'
    : missingProspectName
      ? "Add the prospect's name first"
      : null;

  const hasSubmittedApplicant = applicants.some((a) => a.status === 'submitted');
  const sendInitialApprovalDisabled = isLeadClosed || !hasSubmittedApplicant || isSendingInitialApproval;
  const sendInitialApprovalDisabledReason = isLeadClosed
    ? 'Lead is closed'
    : !hasSubmittedApplicant
      ? 'No submitted applicants yet'
      : null;

  const handleDecisionSuccess = (siblingCount: number, siblingIds: string[]) => {
    onLeadUpdated();
    if (siblingCount > 0) {
      setSiblingPrompt({ count: siblingCount, ids: siblingIds });
    }
  };

  const handleSendSchedulingLink = async () => {
    setSchedulingLinkError(null);
    setIsMintingScheduleLink(true);
    try {
      const result = await mintScheduleToken(lead.id);
      setSchedulingLinkModal(result);
    } catch (err) {
      setSchedulingLinkError(err instanceof Error ? err.message : 'Failed to generate scheduling link');
    } finally {
      setIsMintingScheduleLink(false);
    }
  };

  const handleSendApplicationLink = async () => {
    setApplicationLinkError(null);
    setApplicationLinkSentTo(null);
    setIsSendingApplicationLink(true);
    try {
      await sendApplicationLink(lead.id);
      setApplicationLinkSentTo(lead.prospectEmail);
    } catch (err) {
      // Typed 4xx errors (LEAD_CLOSED / LEAD_MISSING_PROSPECT_NAME) surface their
      // server `message` verbatim here via leasingFetch — not swallowed as generic.
      setApplicationLinkError(err instanceof Error ? err.message : 'Failed to send application link');
    } finally {
      setIsSendingApplicationLink(false);
    }
  };

  const handleSendInitialApproval = async () => {
    setInitialApprovalError(null);
    setIsSendingInitialApproval(true);
    try {
      const result = await sendInitialApproval(lead.id);
      setInitialApprovalNotifiedCount(result.applicantsNotified);
      onLeadUpdated();
    } catch (err) {
      setInitialApprovalError(err instanceof Error ? err.message : 'Failed to send initial approval');
    } finally {
      setIsSendingInitialApproval(false);
    }
  };

  const currentStageOrder = STAGE_ORDER[lead.stage];

  return (
    <>
      {/* AC-L14: marks the header's natural flow position for the IntersectionObserver
          above — see the `isPinned` state comment for why this replaces native sticky. */}
      <div ref={sentinelRef} aria-hidden="true" />
      {/* Reserves the header's flow height while pinned so content below doesn't jump
          when the header switches to `fixed` and leaves normal flow. */}
      {isPinned && <div style={{ height: pinnedHeight }} aria-hidden="true" />}
      <section
        ref={headerRef}
        data-testid="lead-detail-header"
        // AC-L14: below md, TopBar (components/navigation/TopBar.tsx) is ALSO pinned to the
        // viewport top with a higher z-index (40 vs this section's 20) — once this header
        // pins, it would otherwise land at the exact same y=0 and get covered by TopBar.
        // Offset by the mobile top bar's height below md; md+ has no TopBar, so top-0 is
        // correct there. Pinning itself is done via `fixed` + IntersectionObserver (see
        // `isPinned`'s declaration) rather than `position: sticky`, which cannot work here.
        // The `fixed` insets below intentionally mirror AppShell.tsx's `<main>` offsets
        // (`md:ml-[var(--sidebar-width)]` / `lg:pr-[var(--workflow-rail-width,3rem)]`) so a
        // pinned header lines up with the content column instead of the full viewport.
        className={
          (isPinned
            ? 'fixed left-0 right-0 md:left-[var(--sidebar-width)] lg:right-[var(--workflow-rail-width,3rem)] transition-[right] duration-300 ease-in-out '
            : '') +
          'top-[var(--topbar-height)] md:top-0 z-20 bg-background border-b border-border mb-6 p-5'
        }
      >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {lead.prospectName ?? lead.prospectEmail}
          </h1>
          <p
            className="text-sm text-muted-foreground mt-0.5 inline-flex items-center gap-1"
            data-testid="lead-detail-email"
          >
            {lead.prospectEmail}
            <Button
              type="button"
              variant="ghost"
              size="icon"
              data-testid="copy-lead-email-btn"
              onClick={() => void emailCopy.copy(lead.prospectEmail)}
              className="min-h-[44px] min-w-[44px] text-muted-foreground hover:text-foreground"
              aria-label={emailCopy.copied ? 'Email copied' : 'Copy prospect email'}
            >
              {emailCopy.copied ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
            </Button>
          </p>
          {lead.prospectPhone && (
            <p className="text-sm text-muted-foreground inline-flex items-center gap-1" data-testid="lead-detail-phone">
              {lead.prospectPhone}
              <Button
                type="button"
                variant="ghost"
                size="icon"
                data-testid="copy-lead-phone-btn"
                onClick={() => void phoneCopy.copy(lead.prospectPhone!)}
                className="min-h-[44px] min-w-[44px] text-muted-foreground hover:text-foreground"
                aria-label={phoneCopy.copied ? 'Phone copied' : 'Copy prospect phone'}
              >
                {phoneCopy.copied ? <Check className="h-3.5 w-3.5" aria-hidden="true" /> : <Copy className="h-3.5 w-3.5" aria-hidden="true" />}
              </Button>
            </p>
          )}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <span
            data-testid="lead-stage-badge"
            className={badgeVariants({ variant: STAGE_COLORS[lead.stage] })}
          >
            {STAGE_LABELS[lead.stage]}
          </span>
          {/* AC-L5: the send-CTA buttons below are secondary to identity + current stage —
              collapse them behind a toggle at <768px so the sticky header takes less of
              the initial mobile viewport; md+ always shows them (there's room). */}
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="md:hidden min-h-[44px]"
            data-testid="lead-header-actions-toggle"
            onClick={() => setActionsExpanded((v) => !v)}
            aria-expanded={actionsExpanded}
            aria-controls="lead-header-actions"
          >
            {actionsExpanded ? 'Hide actions' : 'Actions'}
          </Button>
        </div>
        <div
          id="lead-header-actions"
          className={`${actionsExpanded ? 'flex' : 'hidden'} md:flex items-center gap-2 flex-wrap mt-2 md:mt-0`}
        >
          {/* Send scheduling link CTA */}
          {canSendSchedulingLink && (
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="send-scheduling-link-btn"
              onClick={() => void handleSendSchedulingLink()}
              disabled={isMintingScheduleLink}
              aria-label="Send scheduling link to prospect"
            >
              {isMintingScheduleLink ? 'Generating…' : 'Send scheduling link'}
            </Button>
          )}
          {/* Send application link CTA (Change 16 / AC-4) */}
          <div className="flex flex-col gap-1 items-start">
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="send-application-link-btn"
              className="min-h-[44px]"
              onClick={() => void handleSendApplicationLink()}
              disabled={sendApplicationLinkDisabled}
              aria-disabled={sendApplicationLinkDisabled}
              aria-describedby={sendApplicationLinkDisabledReason ? 'send-application-link-disabled-hint' : undefined}
              aria-label="Send application link to prospect"
              title={sendApplicationLinkDisabledReason ?? undefined}
            >
              {isSendingApplicationLink ? 'Sending…' : 'Send application link'}
            </Button>
            {sendApplicationLinkDisabledReason && (
              <p
                id="send-application-link-disabled-hint"
                data-testid="send-application-link-disabled-hint"
                className="text-xs text-muted-foreground"
              >
                {sendApplicationLinkDisabledReason}
              </p>
            )}
          </div>
          {/* Send initial approval CTA (US-IA-1) */}
          <div className="flex flex-col gap-1 items-start">
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="send-initial-approval-btn"
              className="min-h-[44px]"
              onClick={() => void handleSendInitialApproval()}
              disabled={sendInitialApprovalDisabled}
              aria-disabled={sendInitialApprovalDisabled}
              aria-describedby={sendInitialApprovalDisabledReason ? 'send-initial-approval-disabled-hint' : undefined}
              aria-label="Send initial approval to submitted applicants"
              title={sendInitialApprovalDisabledReason ?? undefined}
            >
              {isSendingInitialApproval ? 'Sending…' : 'Send Initial Approval'}
            </Button>
            {lead.initialApprovalSentAt && (
              <p
                data-testid="initial-approval-sent-indicator"
                className="text-xs text-muted-foreground"
                role="status"
              >
                {initialApprovalNotifiedCount !== null
                  ? `Initial approval sent to ${initialApprovalNotifiedCount} applicant${initialApprovalNotifiedCount === 1 ? '' : 's'} on ${formatTimestamp(asIsoDateTimeString(lead.initialApprovalSentAt), propertyTimezone)}`
                  : `Initial approval sent on ${formatTimestamp(asIsoDateTimeString(lead.initialApprovalSentAt), propertyTimezone)}`}
              </p>
            )}
            {sendInitialApprovalDisabledReason && (
              <p
                id="send-initial-approval-disabled-hint"
                data-testid="send-initial-approval-disabled-hint"
                className="text-xs text-muted-foreground"
              >
                {sendInitialApprovalDisabledReason}
              </p>
            )}
          </div>
          {/* Improve the agent (IMP-BR-6 entry 3): secondary, renders only when the lead's case has
              had at least one agent run. A lead IS its case (Lead.id = Case.id). */}
          <ImproveAgentButton
            caseId={lead.id}
            request={{
              start: { entry: 'case', anchor: { kind: 'none', caseId: lead.id }, caseId: lead.id },
              agentKind: 'leasing',
              subtitle: `About ${lead.prospectName ?? lead.prospectEmail}`,
            }}
            className="min-h-[44px] font-semibold"
          />
        </div>
      </div>

      {/* Stage stepper (display-only — clicking a stage dot does NOT trigger navigation).
          AC-L4: narrow-chrome element (not a data-dense container), so it stays
          scroll-based at every breakpoint — reuses ScrollFadeTabs' horizontal-scroll +
          edge-fade + auto-scroll-active-into-view pattern rather than going full-width. */}
      <nav aria-label="Lead stage progress" className="mt-4">
        <ScrollFadeTabs activeValue={lead.stage}>
        <ol
          data-testid="lead-stage-stepper"
          className="flex items-center gap-0"
        >
          {STAGE_ORDER_LIST.map((stage, index) => {
            const stageOrder = STAGE_ORDER[stage];
            const isCurrent = stage === lead.stage;
            const isPast = stageOrder < currentStageOrder;
            const isFuture = stageOrder > currentStageOrder;

            return (
              <li
                key={stage}
                data-testid={`stage-step-${stage}`}
                aria-current={isCurrent ? 'step' : undefined}
                data-state={isCurrent ? 'active' : undefined}
                className="flex items-center"
              >
                {/* Stage dot */}
                <div className="flex flex-col items-center gap-1">
                  <div
                    className={[
                      'h-3 w-3 rounded-full border-2 transition-colors',
                      isCurrent
                        ? 'border-primary bg-primary'
                        : isPast
                          ? 'border-primary bg-primary/50'
                          : isFuture
                            ? 'border-muted-foreground/40 bg-transparent'
                            : '',
                    ].join(' ')}
                    aria-hidden="true"
                  />
                  <span
                    className={[
                      'text-xs whitespace-nowrap',
                      isCurrent
                        ? 'font-semibold text-foreground'
                        : isPast
                          ? 'text-muted-foreground'
                          : 'text-muted-foreground/50',
                    ].join(' ')}
                  >
                    {STAGE_LABELS[stage]}
                  </span>
                </div>
                {/* Connector line (not after last item) */}
                {index < STAGE_ORDER_LIST.length - 1 && (
                  <div
                    className={[
                      'h-0.5 w-6 sm:w-10 mx-1 mt-[-14px]',
                      stageOrder < currentStageOrder
                        ? 'bg-primary/50'
                        : 'bg-muted-foreground/20',
                    ].join(' ')}
                    aria-hidden="true"
                  />
                )}
              </li>
            );
          })}
        </ol>
        </ScrollFadeTabs>
      </nav>

      {schedulingLinkError && (
        <div
          className="mt-3 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          role="alert"
          data-testid="scheduling-link-error"
        >
          {schedulingLinkError}
        </div>
      )}

      {applicationLinkSentTo && (
        <div
          className="mt-3 p-3 bg-success/10 border border-success/30 rounded-md text-success text-sm"
          role="status"
          data-testid="send-application-link-success"
        >
          Application link sent to {applicationLinkSentTo}
        </div>
      )}

      {applicationLinkError && (
        <div
          className="mt-3 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          role="alert"
          data-testid="send-application-link-error"
        >
          {applicationLinkError}
        </div>
      )}

      {initialApprovalError && (
        <div
          className="mt-3 p-3 bg-destructive/10 border border-destructive/30 rounded-md text-destructive text-sm"
          role="alert"
          data-testid="send-initial-approval-error"
        >
          {initialApprovalError}
        </div>
      )}

      {/* Stage advance buttons (non-closed leads) — US-44 gating */}
      {!isClosed && (transitions.length > 0 || isDeciding) && (
        <div className="mt-4 space-y-2" data-testid="lead-advance-buttons">
          <div className="flex flex-wrap gap-2">
            {transitions.map((to) => {
              // Determine per-button gating (US-44)
              const isVerifyingTarget = to === 'verifying';
              const isDecidingTarget = to === 'deciding';
              const gatingDisabled =
                (isVerifyingTarget && !verifyingEnabled) ||
                (isDecidingTarget && !decidingEnabled);
              const isDisabled = isAdvancing || gatingDisabled;
              // AC-L13: with zero applicants, the disabled button ("Move to Verifying")
              // and its hint paragraph ("Add and have at least one applicant submit...")
              // said the same thing twice. The hint is more informative, so it's kept and
              // the redundant button hidden — but only in this specific zero-applicant
              // case; once there ARE applicants but gating still isn't satisfied, the
              // button stays visible as the stable target a landlord expects to watch
              // become enabled as applicants progress.
              const hideRedundantButton = applicants.length === 0 && gatingDisabled;

              return (
                <div key={to} className="flex flex-col gap-1">
                  {!hideRedundantButton && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      data-testid={`lead-advance-to-${to}`}
                      onClick={() => onAdvance(to)}
                      disabled={isDisabled}
                      aria-disabled={isDisabled}
                      aria-describedby={
                        isVerifyingTarget && gatingDisabled
                          ? 'advance-verifying-disabled-hint'
                          : isDecidingTarget && gatingDisabled
                            ? 'advance-deciding-disabled-hint'
                            : undefined
                      }
                      aria-label={`Advance lead to ${STAGE_LABELS[to]}`}
                    >
                      {isAdvancing ? 'Advancing…' : `Move to ${STAGE_LABELS[to]}`}
                    </Button>
                  )}
                  {isVerifyingTarget && gatingDisabled && (
                    <p
                      id="advance-verifying-disabled-hint"
                      data-testid="advance-verifying-disabled-hint"
                      className="text-xs text-muted-foreground"
                    >
                      Add and have at least one applicant submit their application to advance.
                    </p>
                  )}
                  {isDecidingTarget && gatingDisabled && (
                    <p
                      id="advance-deciding-disabled-hint"
                      data-testid="advance-deciding-disabled-hint"
                      className="text-xs text-muted-foreground"
                    >
                      All non-withdrawn applicants must be submitted and all non-waived verification tasks must be passed or failed to advance.
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Decision panel — for non-closed leads */}
      {!isClosed && (
        <div
          data-testid="decision-panel"
          className="mt-4 flex flex-wrap gap-2 items-center"
        >
          {isDeciding ? (
            <Button
              type="button"
              size="sm"
              data-testid="lead-record-decision"
              onClick={() => setShowDecisionModal(true)}
              aria-label="Record decision for this lead"
            >
              Record Decision
            </Button>
          ) : (
            // Pre-deciding stages (inquiring/showing/applying/verifying) — US-24 spec calls
            // this "Close Lead", not "Skip to Decision" (issue #1014).
            <Button
              type="button"
              size="sm"
              variant="outline"
              data-testid="lead-close-lead-early"
              onClick={() => setShowDecisionModal(true)}
              aria-label="Close lead"
            >
              Close Lead
            </Button>
          )}
        </div>
      )}

      {/* Closed lead controls */}
      {isClosed && (
        <ClosedLeadControls
          lead={lead}
          isPlatformAdmin={isPlatformAdmin}
          onUpdated={onLeadUpdated}
        />
      )}

      {/* Decision modal */}
      {showDecisionModal && (
        <DecisionModal
          leadId={lead.id}
          onClose={() => setShowDecisionModal(false)}
          onSuccess={handleDecisionSuccess}
        />
      )}

      {/* Sibling-close modal */}
      {siblingPrompt && (
        <SiblingCloseModal
          leadId={lead.id}
          siblingCount={siblingPrompt.count}
          siblingIds={siblingPrompt.ids}
          onClose={() => setSiblingPrompt(null)}
          onSuccess={onLeadUpdated}
        />
      )}

      {/* Scheduling link modal */}
      {schedulingLinkModal && (
        <SchedulingLinkModal
          tokenUrl={schedulingLinkModal.tokenUrl}
          expiresAt={schedulingLinkModal.expiresAt}
          leadEmail={lead.prospectEmail}
          onClose={() => setSchedulingLinkModal(null)}
        />
      )}
      </section>
    </>
  );
}
